/* 0.47.73 - A Number field's Min and Max, checked.
 *
 * Min and Max used to reach the browser as an HTML hint only: a value
 * outside them was accepted, counted as filled and calculated with. Now
 * a value below Min or above Max is an error on the field (shown when
 * the field is left, and by "show required errors"), and the field does
 * not count as filled for "all required fields are filled".
 *
 * The messages are the element's own error_min / error_max (translatable,
 * library values allowed), with {min} and {max} replaced, else
 * "At least {min}" / "At most {max}". */

import { isExpression, extractExpression, evaluateExpression } from './rules/expression';
import { readValue } from './readValue';
// Used at call time only (evaluate.js imports this module too).
import { buildExpressionFunctions } from './rules/evaluate';
import { policyProblems, POLICY_MESSAGE, MATCH_MESSAGE } from './passwordPolicy';

const num = (x) => {
  if (x === undefined || x === null || x === '') return null;
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  const s = String(x).trim().replace(/[\s  ,]/g, '');
  if (s === '' || s === '-' || s === '.') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/* 0.47.74 - Min and Max may be formulas on the form's values ("=claims"
   or "=claims - 1"): a limit follows another field. A formula that
   gives no number (the field still empty) sets no limit. */
function limit(raw, values, functions) {
  /* 0.47.77 - {name} reads a field or a form variable (Settings,
     Variables), as everywhere else: "{max_allowed}". Inside a formula
     too: "={max_allowed} - 1". */
  if (typeof raw === 'string' && raw.includes('{')) {
    raw = raw.replace(/\{([A-Za-z_][A-Za-z0-9_.]*)\}/g, (m, name) => {
      const v = readValue(values || {}, name);
      return v === undefined || v === null ? '' : String(v);
    });
    if (raw.trim() === '' || raw.trim() === '=') return null;
  }
  if (typeof raw === 'string' && isExpression(raw)) {
    const src = extractExpression(raw);
    /* 0.47.80 - A formula whose fields are all still empty sets no
       limit (max(a, b) of two empty fields would give 0, a Max of 0). */
    const names = (src.replace(/"(?:[^"\\]|\\.)*"/g, '')
      .match(/[A-Za-z_][A-Za-z0-9_.]*(?!\s*\()/g) || [])
      .filter(n => !/^(true|false)$/i.test(n));
    if (names.length > 0 && names.every(n => {
      const v = readValue(values || {}, n);
      return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    })) return null;
    try {
      /* 0.47.80 - The platform's formula functions (max, min, if,
         round, lib...), also where the caller passed none. */
      const fns = functions || buildExpressionFunctions({ values: values || {} });
      return num(evaluateExpression(src, values || {}, fns));
    } catch {
      return null;
    }
  }
  return num(raw);
}

/* 0.48.00 - Allowed characters of a Text or Long text field
 * (pattern_preset): a value with anything else is an error on the
 * field, handled exactly as a Number outside its Min / Max (shown when
 * the field is left and with "show required errors", the form not
 * complete until it fits, a call API rule reading the field not made).
 * Letters are any alphabet's (Armenian, Cyrillic, Latin...). The
 * message is the element's error_format, else the preset's own. */
export const PATTERN_TYPES = new Set(['text', 'textarea', 'password']);
export const PATTERN_PRESETS = {
  words: { label: 'Letters, digits and spaces',
           re: /^[\p{L}\p{M}\p{N}\s]*$/u,
           message: 'Use letters, digits and spaces only.' },
  text: { label: 'Letters, digits, spaces and . , - \' " ( ) & : ! ?',
          re: /^[\p{L}\p{M}\p{N}\s.,'"()&:!?«»\u2019\-]*$/u,
          message: 'Use letters, digits, spaces and . , - \' " ( ) & : ! ? only.' },
  digits: { label: 'Digits only', re: /^[0-9]*$/, message: 'Use digits only.' },
};

const _customCache = new Map();
export function customPattern(src) {
  if (_customCache.has(src)) return _customCache.get(src);
  let re = null;
  try { re = new RegExp(`^(?:${src})$`, 'u'); } catch { re = null; }
  _customCache.set(src, re);
  return re;
}

/* 0.48.30 - A Password field's own checks: the password policy, and
   being the same as another field (the confirmation). */
function passwordChecks(el) {
  return !!(el && el.type === 'password' && (el.password_policy || el.match_field));
}

export function hasPattern(el) {
  if (passwordChecks(el)) return true;
  if (!el || !PATTERN_TYPES.has(el.type)) return false;
  const p = el.pattern_preset;
  if (p === 'custom') return !!(el.pattern && customPattern(String(el.pattern)));
  return !!PATTERN_PRESETS[p];
}

export function patternError(el, value) {
  if (!el || !PATTERN_TYPES.has(el.type) || !el.pattern_preset) return null;
  if (el.pattern_preset !== 'custom' && !PATTERN_PRESETS[el.pattern_preset]) return null;
  if (el.pattern_preset === 'custom' && !(el.pattern && customPattern(String(el.pattern)))) return null;
  const v = value == null ? '' : String(value);
  if (v === '') return null;
  const re = el.pattern_preset === 'custom' ? customPattern(String(el.pattern)) : PATTERN_PRESETS[el.pattern_preset].re;
  return re && !re.test(v) ? { kind: 'pattern' } : null;
}

export function numberLimits(el, values, functions) {
  const min = limit(el && el.min, values, functions);
  let max = limit(el && el.max, values, functions);
  /* 0.47.78 - A safeguard: a Max below the Min (a calculation gone
     wrong, a variable not yet set right) is dropped, so the field is
     never impossible to fill. The Min still holds. */
  if (min !== null && max !== null && max < min) max = null;
  return { min, max };
}

/* null, or { kind: 'min' | 'max', limit } for a value outside the range.
 * An empty value is not a range error (Required decides that). */
export function numberRangeError(el, value, values, functions) {
  // 0.48.00 - A Text field's allowed characters, through the same path.
  if (el && PATTERN_TYPES.has(el.type)) {
    const pe = patternError(el, value);
    if (pe || el.type !== 'password') return pe;
    // 0.48.30 - The policy, then the confirmation.
    const v = value == null ? '' : String(value);
    if (v === '') return null;
    if (el.password_policy && policyProblems(v).length) return { kind: 'policy' };
    if (el.match_field && v !== String((values && values[el.match_field]) ?? '')) return { kind: 'match' };
    return null;
  }
  if (!el || el.type !== 'number') return null;
  const v = num(value);
  if (v === null) return null;
  const { min, max } = numberLimits(el, values, functions);
  if (min !== null && v < min) return { kind: 'min', limit: min };
  if (max !== null && v > max) return { kind: 'max', limit: max };
  return null;
}

/* The raw (unresolved) message: the element's text, else the default. */
export function numberRangeMessage(el, err, values, functions) {
  if (!err) return '';
  if (err.kind === 'policy') {
    return (el.error_policy && String(el.error_policy).trim()) ? String(el.error_policy) : POLICY_MESSAGE;
  }
  if (err.kind === 'match') {
    return (el.error_match && String(el.error_match).trim()) ? String(el.error_match) : MATCH_MESSAGE;
  }
  if (err.kind === 'pattern') {
    const own = el.error_format && String(el.error_format).trim();
    if (own) return String(el.error_format);
    const pr = PATTERN_PRESETS[el.pattern_preset];
    return pr ? pr.message : 'This value contains characters that are not allowed.';
  }
  const { min, max } = numberLimits(el, values, functions);
  const own = err.kind === 'min' ? el.error_min : el.error_max;
  const base = (own && String(own).trim())
    ? String(own)
    : (err.kind === 'min' ? 'At least {min}' : 'At most {max}');
  return base
    .replace(/\{min\}/g, min === null ? '' : String(min))
    .replace(/\{max\}/g, max === null ? '' : String(max));
}

/* 0.47.82 - {min} and {max} in a message that was not text yet when the
 * error was flagged (a library value, "=lib(...)", resolved at display):
 * filled in after it is resolved. Text without them is returned as is. */
export function fillRangePlaceholders(el, text, values, functions) {
  if (!el || el.type !== 'number' || typeof text !== 'string') return text;
  if (!/\{(min|max)\}/.test(text)) return text;
  const { min, max } = numberLimits(el, values, functions);
  return text
    .replace(/\{min\}/g, min === null ? '' : String(min))
    .replace(/\{max\}/g, max === null ? '' : String(max));
}

/* With Min 0 or more, a minus sign cannot make a valid value: typing it
 * is refused (the key does nothing). A pasted negative number is kept
 * as pasted and shows the Min error, so the citizen sees why. */
export function minusBlocked(el, values, functions) {
  const { min } = numberLimits(el, values, functions);
  return min !== null && min >= 0;
}

const _isEmpty = (v) => (
  v === undefined || v === null || v === ''
  || (Array.isArray(v) && v.length === 0)
);

function _walk(structure, cb) {
  (structure || []).forEach(el => {
    if (!el || typeof el !== 'object') return;
    cb(el);
    if (Array.isArray(el.children)) _walk(el.children, cb);
  });
}

/* Number fields by Element ID. */
export function numberFieldIndex(structure) {
  const m = new Map();
  _walk(structure, el => {
    if (el.type !== 'number' && !hasPattern(el)) return;
    const eid = el.element_id || el.name || '';
    if (eid) m.set(eid, el);
  });
  return m;
}

/* For "show required errors": flag every visible Number field whose
 * value is outside its Min / Max (required or not) in target.errors /
 * target.showError, remembered in target.rangeFlags. True when it
 * flagged one. */
export function flagRangeErrors(structure, values, ruleState, target) {
  const hidden = (ruleState && ruleState.hidden) || new Set();
  let mutated = false;
  numberFieldIndex(structure).forEach((el, eid) => {
    if (el.hidden || hidden.has(eid)) return;
    const err = numberRangeError(el, values ? values[eid] : undefined, values);
    if (!err) return;
    target.errors.set(eid, numberRangeMessage(el, err, values));
    target.showError.add(eid);
    if (!target.rangeFlags) target.rangeFlags = new Set();
    target.rangeFlags.add(eid);
    mutated = true;
  });
  return mutated;
}

/* Flag one field (left by the citizen, or pasted into). Returns the
 * next sticky state, or prev when nothing changes. */
export function flagOneRangeError(prev, el, value, values) {
  const eid = el && (el.element_id || el.name);
  if (!eid) return prev;
  // 0.47.78 - Remember the field was left once: when a limit that
  // follows another field moves later, its error shows at once.
  const touched = prev.rangeTouched && prev.rangeTouched.has(eid)
    ? prev.rangeTouched : new Set([...(prev.rangeTouched || []), eid]);
  const err = numberRangeError(el, value, values);
  if (!err) return touched === prev.rangeTouched ? prev : { ...prev, rangeTouched: touched };
  const msg = numberRangeMessage(el, err, values);
  if (prev.showError.has(eid) && prev.errors.get(eid) === msg && touched === prev.rangeTouched) return prev;
  const showError = new Set(prev.showError); showError.add(eid);
  const errors = new Map(prev.errors); errors.set(eid, msg);
  const rangeFlags = new Set(prev.rangeFlags || []); rangeFlags.add(eid);
  return { ...prev, showError, errors, rangeFlags, rangeTouched: touched };
}

/* The release valve for flagged fields, with Number ranges: a Number
 * field still out of range keeps its error (its message updated when
 * it went from below Min to above Max); a field flagged for its range
 * alone clears when emptied; any other flagged field clears once it
 * has a value, as before. */
export function releaseFlags(prev, values, structure) {
  const touched = prev.rangeTouched || new Set();
  if (prev.showError.size === 0 && prev.errors.size === 0 && touched.size === 0) return prev;
  const idx = numberFieldIndex(structure);
  const rangeFlags = new Set(prev.rangeFlags || []);
  let changed = false;
  const nextShow = new Set(prev.showError);
  const nextErr = new Map(prev.errors);
  /* 0.47.78 - A field already left once that falls outside its range
     because a limit moved (another field, a variable) shows its error
     now, without being left again. */
  for (const eid of touched) {
    if (nextShow.has(eid)) continue;
    const el = idx.get(eid);
    const err = el ? numberRangeError(el, values ? values[eid] : undefined, values) : null;
    if (!err) continue;
    nextShow.add(eid);
    nextErr.set(eid, numberRangeMessage(el, err, values));
    rangeFlags.add(eid);
    changed = true;
  }
  for (const eid of prev.showError) {
    const v = values ? values[eid] : undefined;
    const el = idx.get(eid);
    const err = el ? numberRangeError(el, v, values) : null;
    if (err) {
      const msg = numberRangeMessage(el, err, values);
      if (nextErr.get(eid) !== msg) { nextErr.set(eid, msg); changed = true; }
      if (!rangeFlags.has(eid)) { rangeFlags.add(eid); changed = true; }
      continue;
    }
    if (!_isEmpty(v) || rangeFlags.has(eid)) {
      nextShow.delete(eid);
      nextErr.delete(eid);
      rangeFlags.delete(eid);
      changed = true;
    }
  }
  if (!changed) return prev;
  return { ...prev, showError: nextShow, errors: nextErr, rangeFlags };
}
